import {useCallback} from "react";
import type {CreateReviewAction} from "platformed-client/types";
import {useLayerType} from "../../../hooks/layerType";
import {unreachableCase} from "../../../utils/typescript";
import api from "../../../api";
import type {ReviewCascade} from "../../../api/tprm/auditPlans";
import {ReviewItem} from ".";
import {useReviewSource} from "./ReviewSource";

/**
 * Create review actions (request / submit / unrequest) for any review item,
 * routing to the correct API for its type. Single source of truth for the
 * add-reviewer, remove-reviewer and request-new-review flows.
 */
export function useCreateReviews(reviewItem: ReviewItem) {
  const [layerType] = useLayerType();
  const source = useReviewSource();
  return useCallback(
    // `cascade` is AuditPlanPage-only: only audit plan pages have a descendant subtree.
    async (requests: CreateReviewAction[], cascade?: ReviewCascade) => {
      if (source) return source.createReviews(requests);
      switch (reviewItem.type) {
        case "Question":
          await api.vendorToolkit.questions.createReviews(reviewItem.id, layerType, requests);
          break;
        case "Risk":
          await api.tprm.risks.createReviews(reviewItem.id, requests);
          break;
        case "Control":
          await api.tprm.assessedControls.createReviews(reviewItem.id, requests);
          break;
        case "AuditPlanPage":
          await api.tprm.auditPlans.createReviews(reviewItem.engagementId, reviewItem.id, requests, cascade);
          break;
        case "DisclosurePage":
          await api.tprm.disclosures.createPageReviews(reviewItem.engagementId, reviewItem.pages, requests);
          break;
        default:
          unreachableCase(reviewItem);
      }
    },
    [reviewItem, layerType, source],
  );
}
